import { memo } from 'react';
import { SkeletonPlaceholder, SkeletonText } from '@carbon/react';
import cx from 'clsx';

import { routes } from 'core/routing';
import { MEASUREMENT_SYSTEM_LABEL } from 'models/measurementSystem';
import Link from 'shared/components/Link';
import ProjectBreadcrumbs, { PROJECT_BREADCRUMBS_PAGE } from 'shared/components/ProjectBreadcrumbs';
import ProjectTier from 'shared/components/ProjectTier';
import OutputsOverview from 'shared/outputs/OutputsOverview';
import { formatDate } from 'utils/dates';
import styles from 'theme/features/dashboard/dashboard.module.scss';

const DashboardOverview = ({ overview, project, projectId, isCompact, isExport }) => (
  <aside className={cx(styles.overview, isCompact && styles['overview--compact'])}>
    {isExport ? null : isCompact ? (
      <Link href={routes.projectOutputs(projectId)}>View Project</Link>
    ) : (
      <ProjectBreadcrumbs page={PROJECT_BREADCRUMBS_PAGE.dashboard} projectId={projectId} />
    )}
    <div className={styles['overview__description']}>
      <div>
        <h3>
          {project ? (
            <strong>
              {project.details.name}
              {project.details.tier != null && (
                <ProjectTier
                  tier={project.details.tier}
                  isValid={project.details.tierIsValid}
                  withTooltip
                  size="lg"
                  inline
                />
              )}
            </strong>
          ) : (
            <SkeletonText className={styles['overview__header-skeleton']} />
          )}
        </h3>
        {project ? (
          <p>{project.details.description}</p>
        ) : (
          <SkeletonText className={styles['overview__description-skeleton']} />
        )}
        {project ? (
          <p className={styles['version-label']}>{project.details.isFinal ? 'Final Version' : ''}</p>
        ) : (
          <SkeletonText className={styles['overview__description-skeleton']} />
        )}
        {project ? (
          <p className={styles['version-label']}>{project.details.isConfidential ? 'Confidential' : ''}</p>
        ) : (
          <SkeletonText className={styles['overview__description-skeleton']} />
        )}
        {project ? (
          <p className={styles['overview__created-by']}>
            Created by {project.details.userName} on {formatDate(project.details.createdOn)}
          </p>
        ) : (
          <SkeletonText className={styles['overview__created-by-skeleton']} />
        )}
      </div>
      <br />
      {project ? (
        <div className={styles['overview__details']}>
          <div>
            <div>
              <h5>
                <strong>Site Location</strong>
              </h5>
              <p>
                {project.details.address ? (
                  <>
                    {project.details.address}
                    <br />
                    {[project.details.city, project.details.state, project.details.zipCode]
                      .filter((x) => !!x)
                      .join(', ')}
                  </>
                ) : (
                  project.details.city
                )}
                <br />
                {project.details.countryName}
              </p>
            </div>
            <br />
            <h5>
              <strong>Measurement System</strong>
            </h5>
            <p>{MEASUREMENT_SYSTEM_LABEL[project.details.measurementSystem]}</p>
          </div>
        </div>
      ) : (
        <SkeletonPlaceholder className={styles['overview__details-skeleton']} />
      )}
    </div>
    <br />
    <OutputsOverview overview={overview} project={project} isCompact={isCompact} />
  </aside>
);

export default memo(DashboardOverview);
